import { ChangeEvent, FC, useRef } from "react";
import { Button, Text } from "@foris/avocado-suite";
import styles from "../../i18nCatalog.module.scss";

interface FileUploadButtonProps {
  label: string;
  /** Called only when a file was actually picked. */
  onFile: (file: File) => void;
  isLoading?: boolean;
  /** Name of the file picked last, shown beside the button. */
  fileName?: string;
  accept?: string;
}

/**
 * A button that opens the file picker, hiding the input it drives.
 *
 * The input is cleared on every change so picking the same file twice in a row still
 * fires `onChange` — without that, retrying a failed upload silently does nothing.
 */
const FileUploadButton: FC<FileUploadButtonProps> = ({
  label,
  onFile,
  isLoading = false,
  fileName,
  accept = ".xlsx",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (file) onFile(file);
  };

  return (
    <div className={styles.i18nCatalog_actions}>
      <input
        accept={accept}
        className={styles.i18nCatalog_fileInput}
        onChange={handleChange}
        ref={inputRef}
        type="file"
      />
      <Button
        leftIcon="upload"
        loading={isLoading}
        onClick={() => inputRef.current?.click()}
        variant="primary"
      >
        {label}
      </Button>

      {!!fileName && (
        <Text className={styles.i18nCatalog_fileName} type="sm">
          {fileName}
        </Text>
      )}
    </div>
  );
};

export default FileUploadButton;
